import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CircularProgress, makeStyles } from '@material-ui/core';
import PageContent from 'components/Generic/PageContent';
import { useStockConditionneRepreneurWithFilter } from 'hooks';
import { DialogWidth, StockConditionneRepreneurByBuyerThenMaterial, StockConditionneRepreneurFilters } from 'types';
import { transformObjectId } from 'services/apiPlatformTransformer';
import { AppContext } from 'context';
import Dialog from 'components/Dialog';
import { useLatestClosureDate } from 'utils/closureHooks';
import { ConditionneParRepreneurTable } from './ConditionneParRepreneurTable';
import FilterStockConditionneRepreneurForm from './FilterStockConditionneRepreneurForm';
import { exportStockConditionneRepreneur } from './services';

const useStyles = makeStyles({
  downloadModalSubtitle: {
    marginTop: 0,
    fontWeight: 600,
    fontSize: 14,
  },
});

const StockConditionneParRepreneurPage: React.FC = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const context = useContext(AppContext);
  const sortingCenter = context.getSortingCenter?.();
  const sortingCenterId = transformObjectId(sortingCenter);

  const [stockConditionneRepreneurData, setStockConditionneRepreneurData] = useState<
    StockConditionneRepreneurByBuyerThenMaterial | [] | null
  >(null);
  const [filter, setFilter] = useState<StockConditionneRepreneurFilters>({});
  const [exportButtonDisabled, setExportButtonDisabled] = useState(false);
  const [downloadModalVisible, setDownloadModalVisible] = useState<boolean>(false);
  const stockConditionneRepreneurBatch = useStockConditionneRepreneurWithFilter(filter);
  const now = new Date();
  const [, twoMonthAfterClosureDate] = useLatestClosureDate(sortingCenter);
  const dateOfStockConditionneRepreneur =
    null === twoMonthAfterClosureDate || now < twoMonthAfterClosureDate ? now : twoMonthAfterClosureDate;

  useEffect(() => {
    if (null === stockConditionneRepreneurBatch) return;

    setStockConditionneRepreneurData(stockConditionneRepreneurBatch);
  }, [stockConditionneRepreneurBatch]);

  useEffect(() => {
    setExportButtonDisabled(!Boolean(stockConditionneRepreneurBatch?.length));
  }, [stockConditionneRepreneurBatch?.length]);

  const handleSubmit = (filters: StockConditionneRepreneurFilters) => {
    const filtersFormatted = {
      buyerId: filters.buyerId ?? '',
      materialId: filters.materialId ?? '',
    };
    setFilter(filtersFormatted);
  };

  const onExport = () => {
    if (!sortingCenterId) return;
    const queryParams = { ...filter, dateOfStockConditionneRepreneur };
    exportStockConditionneRepreneur(sortingCenterId, queryParams, t, setDownloadModalVisible);
  };

  return (
    <PageContent
      title={t('stockConditionneParRepreneurPage.title')}
      exportButtonHandler={onExport}
      exportButtonDisabled={exportButtonDisabled}
    >
      <Dialog
        closable={false}
        renderButton={() => {}}
        isOpen={downloadModalVisible}
        width={DialogWidth.SMALL_FORM}
        renderContent={() => (
          <>
            <h1
              style={{
                fontWeight: 600,
                fontSize: 24,
              }}
            >
              {t('stockConditionneParRepreneurPage.export.title')}
            </h1>
            <h2 className={classes.downloadModalSubtitle}>{t('stockConditionneParRepreneurPage.export.subtitle')}</h2>
            <CircularProgress style={{ marginBottom: 10 }} size={49} thickness={6} />
          </>
        )}
      />
      <FilterStockConditionneRepreneurForm
        filters={filter}
        onSubmit={handleSubmit}
        dateForMessage={dateOfStockConditionneRepreneur}
      />
      <ConditionneParRepreneurTable stockConditionneByBuyerThenMaterial={stockConditionneRepreneurData} />
    </PageContent>
  );
};
export default StockConditionneParRepreneurPage;
